import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ZmanimData } from '../types';
import { computeZmanimForDate } from '../utils/hebcalZmanim';
import { normalizeZmanim } from '../utils/zmanimRules';
import { addDaysToDate } from '../utils/israelTime';

/**
 * A `getZmanim(date)` function for resolveOpeningHours(): uses the /zmanimCache
 * doc (MyZmanim) when there is one for that date, otherwise Hebcal.
 * Cache docs are loaded for `days` dates starting at `startDate`; until they
 * arrive (or for other dates) Hebcal is used, so the getter always answers.
 */
export function useZmanimGetter(startDate: string, days: number): (date: string) => ZmanimData {
  const [cached, setCached] = useState<Record<string, ZmanimData>>({});

  useEffect(() => {
    let cancelled = false;
    const dates = Array.from({ length: days }, (_, i) => addDaysToDate(startDate, i));
    Promise.all(
      dates.map(async (date) => {
        try {
          const snap = await getDoc(doc(db, 'zmanimCache', date));
          return snap.exists() ? ([date, normalizeZmanim(snap.data() as ZmanimData)] as const) : null;
        } catch {
          return null; // Hebcal fallback
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const found: Record<string, ZmanimData> = {};
      for (const r of results) if (r) found[r[0]] = r[1];
      setCached(found);
    });
    return () => {
      cancelled = true;
    };
  }, [startDate, days]);

  return useMemo(() => {
    const hebcal = new Map<string, ZmanimData>();
    return (date: string) => {
      if (cached[date]) return cached[date];
      let z = hebcal.get(date);
      if (!z) {
        z = computeZmanimForDate(date);
        hebcal.set(date, z);
      }
      return z;
    };
  }, [cached]);
}
